import React from 'react';

import MuiAccordion from '@material-ui/core/Accordion';
import MuiAccordionDetails from '@material-ui/core/AccordionDetails';
import MuiAccordionSummary from '@material-ui/core/AccordionSummary';
import { withStyles } from '@material-ui/core/styles';

import { SourceStatus } from './applicant-source-status';
import { ApplicantSourceStatusIcon } from './applicant-source-status-icon';

import { EXTERNAL_APPLICATION_SOURCE_ISSUE_METADATA as ISSUE_METADATA } from '../../util/external-application-source-issue-metadata';
import { ICON_NAMES, MaterialDesignIcon } from '../../util/material-design-icons';

const Accordion = withStyles({
  root: {
    border: '1px solid rgba(0, 0, 0, .125)',
    boxShadow: 'none',
    '&:not(:last-child)': {
      borderBottom: 0,
    },
    '&:before': {
      display: 'none',
    },
    '&$expanded': {
      margin: 'auto',
    },
  },
  expanded: {},
})(MuiAccordion);

const AccordionSummary = withStyles({
  root: {
    backgroundColor: 'rgba(0, 0, 0, .03)',
    borderBottom: '1px solid rgba(0, 0, 0, .125)',
    marginBottom: -1,
    minHeight: 56,
    '&$expanded': {
      minHeight: 56,
    },
  },
  content: {
    '&$expanded': {
      margin: '12px 0',
    },
  },
  expanded: {},
})(MuiAccordionSummary);

const AccordionDetails = withStyles(() => ({
  root: {
    padding: '16px',
  },
}))(MuiAccordionDetails);

const DetailsSection = ({
  label,
  className,
  children,
}: {
  label: string;
  className?: string;
  children: React.ReactNode;
}) => (
  <div className={className}>
    <div className="typo-label">{label}</div>
    <div className="typo-body">{children}</div>
  </div>
);

type InputProps = {
  sourceName: string;
  status?: SourceStatus | null;
  job?: Job;
  org?: Org | null;
  issueTypes?: string[];
  children?: React.ReactNode;
};

export const ApplicantSourceStatusAccordion = ({
  status,
  sourceName,
  issueTypes,
  children,
  job,
  org,
}: InputProps) => {
  const { NEEDS_REVIEW, PENDING, SUCCESS, NA } = SourceStatus;

  const renderSearchOnIndeed = () =>
    sourceName === 'Indeed' ? (
      <div className="u-margin-top">
        <a
          href={`https://www.indeed.com/jobs?q=${encodeURIComponent(
            `${org?.name} ${job?.title}`,
          )}&l=${encodeURI(`${job?.city}, ${job?.state}`)}`}
          className="typo-link"
          target="_blank"
          rel="noreferrer noopener"
        >
          Search on Indeed
        </a>
      </div>
    ) : null;

  const renderDetails = () => {
    switch (status) {
      case SUCCESS:
        return (
          <DetailsSection label="Success!">
            Your job has successfully posted to {sourceName}.{renderSearchOnIndeed()}
          </DetailsSection>
        );
      case PENDING:
        return (
          <DetailsSection label="Pending...">
            {sourceName} is processing your job post. Please wait.
          </DetailsSection>
        );
      case NA:
        return (
          <DetailsSection label="Status Unavailable">
            We post to {sourceName} but it does not provide feedback on the status of your job.
            {renderSearchOnIndeed()}
          </DetailsSection>
        );
      case NEEDS_REVIEW:
        return (
          <div className="u-display-flex u-flexDirection-column">
            {issueTypes?.map((issue) => {
              const metadata = ISSUE_METADATA(issue);
              return (
                <DetailsSection
                  key={`${sourceName}-${issue}`}
                  className="u-margin-bottom"
                  label={metadata.displayName}
                >
                  {metadata.description}
                  {metadata.linkUrl && (
                    <a
                      href={metadata.linkUrl}
                      className="typo-link"
                      target="_blank"
                      rel="noreferrer noopener"
                    >
                      {' '}
                      {metadata.linkText}
                    </a>
                  )}
                  {issue === 'INDEED_NO_VIEWS' && job && org && (
                    <div className="u-margin-top">
                      If you&apos;re able to find and view your job on Indeed, this issue will
                      disappear shortly.{' '}
                    </div>
                  )}
                  {renderSearchOnIndeed()}
                </DetailsSection>
              );
            })}
          </div>
        );
      default:
        return null;
    }
  };

  return (
    <Accordion defaultExpanded={status === SourceStatus.NEEDS_REVIEW} square>
      <AccordionSummary expandIcon={<MaterialDesignIcon iconName={ICON_NAMES.EXPAND_MORE} />}>
        <div className="u-display-flex u-alignItems-center">
          {status === undefined ? (
            <div style={{ minWidth: '24px' }} />
          ) : (
            <ApplicantSourceStatusIcon status={status} />
          )}
          <div className="u-margin-left typo-title">{sourceName}</div>
        </div>
      </AccordionSummary>
      <AccordionDetails>{children || renderDetails()}</AccordionDetails>
    </Accordion>
  );
};
